/**
 * Pure presentation logic for the DE-279 Greengrass component-health pill.
 *
 * Factored out of DeviceStatus.tsx / OpsDeviceGroups.tsx so it can be
 * unit-tested without a render harness — the updateJobs.ts / contentSync.ts
 * pattern.
 *
 * Why this pill exists: the Gateway card's component list comes from the
 * device shadow, which is self-published by the telemetry GG component — a
 * reflashed device (components wiped, shadow frozen) looks healthy there.
 * The pill reflects the GG control plane instead, so "0 installed — run
 * gg-redeploy" shows exactly when the shadow is lying.
 */

/** Only the fields the pill reads — keeps the module free of API types. */
export interface GgHealthLike {
  verdict:
    | 'ok'
    | 'drift'
    | 'unhealthy'
    | 'broken'
    | 'missing'
    | 'needs_redeploy'
    | 'no_target'
    | 'no_core'
    | 'error';
  env_group?: string | null;
  core_status?: 'HEALTHY' | 'UNHEALTHY' | null;
  as_of_iso?: string | null;
  installed?: { name: string; version: string; state: string | null }[];
  target?: { name: string; version: string }[] | null;
  missing?: string[];
  drift?: { name: string; installed: string; target: string }[];
  /** Whether an unhealthy verdict fixes itself (deployment job QUEUED for
   * this device) or needs an operator (SUCCEEDED/no execution — reflash). */
  remediation?: 'auto_on_connect' | 'in_progress' | 'needs_redeploy' | 'attention' | null;
}

/** Palette has no red — 'warn' is the strongest tone; severity rides in the label. */
export type GgTone = 'ok' | 'warn' | 'muted';

export interface GgPill {
  label: string;
  tone: GgTone;
  /** Tooltip body; always ends with the freshness line + staleness caveat. */
  title: string;
}

const CAVEAT =
  'Component list is what the device last reported to AWS — it can lag a reflash by one boot.';

function freshness(h: GgHealthLike): string {
  const asOf = h.as_of_iso ? new Date(h.as_of_iso).toLocaleString() : 'unknown';
  const core = h.core_status ?? 'unreported';
  return `Core ${core} · as of ${asOf}. ${CAVEAT}`;
}

/** "n/m" where m = target components and n = those actually installed. */
export function ggCounts(h: GgHealthLike): { n: number; m: number } | null {
  if (!h.target) return null;
  const m = h.target.length;
  return { n: m - (h.missing?.length ?? 0), m };
}

/**
 * Verdict → pill. Returns null when nothing should render (no data yet, or
 * a query error on a half-deployed stack — degrade silently, never alarm on
 * missing plumbing).
 *
 * `deployInFlight` (a GG rollout job is active on this device) softens
 * missing/drift to a neutral "updating…" — mid-deploy discrepancy is
 * expected, not a fault.
 */
export function ggHealthPill(
  h: GgHealthLike | undefined | null,
  opts?: { deployInFlight?: boolean },
): GgPill | null {
  const base = baseGgPill(h, opts);
  // Remediation overlay — refines WARN pills only (a muted pill is already a
  // no-action state). QUEUED/IN_PROGRESS turn the warning into a calm
  // "resolves itself"; SUCCEEDED/no-execution escalate to the explicit
  // manual fix; FAILED asks for eyes.
  if (!base || !h || base.tone !== 'warn' || !h.remediation) return base;
  switch (h.remediation) {
    case 'auto_on_connect':
      return {
        label: 'GG updates on next connect',
        tone: 'muted',
        title: `${base.title} The component update is queued and installs automatically when the device next comes online — no action needed.`,
      };
    case 'in_progress':
      return {
        label: 'GG updating…',
        tone: 'muted',
        title: `${base.title} Deployment applying now.`,
      };
    case 'needs_redeploy':
      return {
        label: 'GG needs manual redeploy',
        tone: 'warn',
        title: `${base.title} The recorded deployment already succeeded here (reflash fingerprint) — AWS never re-sends it. Fix: scripts/gg-redeploy.sh ${h.env_group ?? '<group>'}.`,
      };
    case 'attention':
      return {
        label: 'GG update failed',
        tone: 'warn',
        title: `${base.title} The latest deployment execution failed on this device — investigate, then redeploy.`,
      };
  }
}

function baseGgPill(
  h: GgHealthLike | undefined | null,
  opts?: { deployInFlight?: boolean },
): GgPill | null {
  if (!h) return null;
  const counts = ggCounts(h);
  const nm = counts ? `${counts.n}/${counts.m}` : '';

  switch (h.verdict) {
    case 'ok':
      return { label: `GG ${nm} up to date`, tone: 'ok', title: `All deployed components installed. ${freshness(h)}` };
    case 'needs_redeploy':
      return {
        label: 'GG no components installed',
        tone: 'warn',
        title: `No artome components installed — reflashed device. Fix: scripts/gg-redeploy.sh ${h.env_group ?? '<group>'}. ${freshness(h)}`,
      };
    case 'missing': {
      if (opts?.deployInFlight) {
        return { label: `GG ${nm} updating…`, tone: 'muted', title: `Deployment in progress. ${freshness(h)}` };
      }
      const names = (h.missing ?? []).join(', ');
      return { label: `GG ${nm} installed`, tone: 'warn', title: `Missing: ${names}. ${freshness(h)}` };
    }
    case 'broken': {
      const names = (h.installed ?? [])
        .filter((c) => c.state === 'BROKEN' || c.state === 'ERRORED')
        .map((c) => c.name)
        .join(', ');
      return { label: 'GG component broken', tone: 'warn', title: `Component broken: ${names || 'unknown'}. ${freshness(h)}` };
    }
    case 'unhealthy':
      return { label: 'GG core unhealthy', tone: 'warn', title: `Components match the deployment but the core reports UNHEALTHY. ${freshness(h)}` };
    case 'drift': {
      if (opts?.deployInFlight) {
        return { label: `GG ${nm} updating…`, tone: 'muted', title: `Deployment in progress. ${freshness(h)}` };
      }
      const names = (h.drift ?? []).map((d) => `${d.name} ${d.installed}→${d.target}`).join(', ');
      return { label: 'GG outdated versions', tone: 'warn', title: `Installed ≠ deployed: ${names}. ${freshness(h)}` };
    }
    case 'no_target':
      return { label: 'GG no deployment', tone: 'muted', title: `No Greengrass deployment targets this device's group. ${freshness(h)}` };
    case 'no_core':
      return { label: 'GG never connected', tone: 'muted', title: 'No Greengrass core record — the nucleus has never reported to AWS.' };
    case 'error':
      return { label: 'GG status unavailable', tone: 'muted', title: 'Greengrass health unavailable (API error).' };
  }
}
